import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldOff } from 'lucide-react';
import { defineAbilitiesFor, type PlatformAction, type PlatformRole, type PlatformSubject } from '@/lib/abilities';
import { useAuthStore } from '@/store/authStore';

export const PLATFORM_ROLE_ORDER: PlatformRole[] = [
    'SUPER_ADMIN',
    'SALES',
    'SUPPORT',
    'FINANCE',
    'COMPLIANCE',
    'DEVOPS',
    'READ_ONLY',
    'AGENT',
];

const ROLE_DEFAULT_LABELS: Record<PlatformRole, string> = {
    SUPER_ADMIN: 'Super admin',
    SALES: 'Sales',
    SUPPORT: 'Support',
    FINANCE: 'Finance',
    COMPLIANCE: 'Compliance',
    DEVOPS: 'DevOps',
    READ_ONLY: 'Viewer',
    AGENT: 'Agent',
};

/** Roles whose frontend ability mirror allows `action` on `subject`. */
export function rolesAllowedTo(action: PlatformAction, subject: PlatformSubject): PlatformRole[] {
    return PLATFORM_ROLE_ORDER.filter((role) => defineAbilitiesFor(role).can(action, subject));
}

/**
 * UX-09: honest 403 rendered inside the app shell when the signed-in role
 * lacks the ability a route needs. Names the operator's role and the roles
 * that do have access, and links back to the dashboard.
 */
export function ForbiddenPage({
    action,
    subject,
}: {
    action?: PlatformAction;
    subject?: PlatformSubject;
}): JSX.Element {
    const { t } = useTranslation();
    const role = useAuthStore((s) => s.user?.role);
    const roleLabel = (r: PlatformRole): string => t(`auth.roles.${r}`, { defaultValue: ROLE_DEFAULT_LABELS[r] });
    const allowed = action && subject ? rolesAllowedTo(action, subject) : [];

    return (
        <div className="flex min-h-[60dvh] items-center justify-center p-4" data-testid="forbidden-page">
            <div className="w-full max-w-md rounded-2xl border border-tg-border bg-white p-8 text-center shadow-[var(--tg-shadow-card)]">
                <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-tg-danger/10 text-tg-danger">
                    <ShieldOff className="h-6 w-6" aria-hidden="true" />
                </div>
                <p className="mt-4 text-3xl font-bold text-tg-ink">403</p>
                <h1 className="mt-1 text-lg font-semibold text-tg-ink">
                    {t('auth.forbidden.title', { defaultValue: 'You do not have access to this page' })}
                </h1>
                {role ? (
                    <p className="mt-2 text-sm text-tg-muted">
                        {t('auth.forbidden.yourRole', { defaultValue: 'Your role: {{role}}', role: roleLabel(role) })}
                    </p>
                ) : null}
                {allowed.length > 0 ? (
                    <p className="mt-1 text-sm text-tg-muted" data-testid="forbidden-required-roles">
                        {t('auth.forbidden.requiredRoles', {
                            defaultValue: 'Needs one of these roles: {{roles}}',
                            roles: allowed.map(roleLabel).join(', '),
                        })}
                    </p>
                ) : null}
                <p className="mt-3 text-sm text-tg-muted">
                    {t('auth.forbidden.askAdmin', { defaultValue: 'Ask a platform admin if you need access.' })}
                </p>
                <Link
                    to="/dashboard"
                    className="mt-6 inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-tg-teal px-4 text-sm font-semibold text-white hover:bg-tg-teal/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tg-teal/50"
                >
                    {t('auth.forbidden.back', { defaultValue: 'Back to dashboard' })}
                </Link>
            </div>
        </div>
    );
}
